/**
 * React hook for offline sync queue status.
 * Provides current online/offline state, queue length, and sync status.
 *
 * Also auto-starts the sync worker on first mount.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  startSyncWorker,
  onSyncStatusChange,
  getQueueStatus,
  queueMutation,
  type SyncQueueStatus,
  type QueuedMutation,
} from '@/services/offlineSyncQueue';

export function useOfflineSync() {
  const [status, setStatus] = useState<SyncQueueStatus>(getQueueStatus);
  const started = useRef(false);

  useEffect(() => {
    if (!started.current) {
      startSyncWorker();
      started.current = true;
    }

    const unsub = onSyncStatusChange(setStatus);
    return unsub;
  }, []);

  const queue = useCallback((mutation: Omit<QueuedMutation, 'id' | 'createdAt' | 'retries'>) => {
    queueMutation(mutation);
  }, []);

  return {
    /** Is the browser online? */
    online: status.online,
    /** Number of queued mutations waiting to sync */
    pendingCount: status.queueLength,
    /** Is a sync currently in progress? */
    syncing: status.syncing,
    /** Timestamp of last successful sync */
    lastSyncAt: status.lastSyncAt,
    /** Last sync error message */
    lastError: status.lastError,
    /** Queue a new mutation for offline replay */
    queueMutation: queue,
  };
}
